import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithClient } from "@/redux/baseQuery";
import {
  ClientScopeArg,
  GetBlockedDriversArgs,
  GetBlockedDriversRawResponse,
  GetBlockedDriversResult,
  GetPreferredDriversRawResponse,
  PreferredDriver,
  RemovePreferredDriverParams,
  UnblockDriverParams,
} from "./types";

export const clientDispatchApi = createApi({
  reducerPath: "clientDispatchApi",
  baseQuery: baseQueryWithClient("dispatch"),
  tagTypes: ["BlockedDrivers", "PreferredDrivers"],
  endpoints: (builder) => ({
    getBlockedDrivers: builder.query<GetBlockedDriversResult, GetBlockedDriversArgs>({
      // Server-side pagination/sorting via HAL `page`/`page_size` (+ `sort`/`order`) query params.
      query: ({ page, pageSize, sort, order, accountId }) => ({
        url: "/blocked-supplier",
        accountId,
        params: { page, page_size: pageSize, ...(sort ? { sort, order: order ?? "asc" } : {}) },
      }),
      transformResponse: (response: GetBlockedDriversRawResponse): GetBlockedDriversResult => ({
        items: (response?._embedded?.items ?? []).map((item) => ({
          restrictionUuid: item.restriction_uuid,
          driverId: item.driver_id,
          driverName: item.driver_name,
          reason: item.reason,
        })),
        totalItems: response?.total_items ?? 0,
        pageCount: response?.page_count ?? 1,
        page: response?.page ?? 1,
      }),
      providesTags: ["BlockedDrivers"],
    }),
    unblockDriver: builder.mutation<void, UnblockDriverParams>({
      query: ({ restrictionUuid, accountId }) => ({
        url: `/blocked-supplier/${restrictionUuid}`,
        method: "DELETE",
        accountId,
      }),
      invalidatesTags: ["BlockedDrivers"],
    }),
    getPreferredDrivers: builder.query<PreferredDriver[], ClientScopeArg | void>({
      // This endpoint has no server-side sorting (unlike blocked-supplier), so fetch the full
      // list in one go — same as the mobile app — and let the table sort/paginate client-side.
      query: (arg) => ({
        url: "/preferred-supplier",
        accountId: arg?.accountId,
        params: { page_size: 10000 },
      }),
      transformResponse: (response: GetPreferredDriversRawResponse): PreferredDriver[] =>
        (response?._embedded?.items ?? []).map((item) => ({
          accountUuid: item.account_uuid,
          driverId: item.supplier?.share_code ?? "",
          driverName: item.supplier?.name ?? "",
        })),
      providesTags: ["PreferredDrivers"],
    }),
    removePreferredDriver: builder.mutation<void, RemovePreferredDriverParams>({
      query: ({ preferredAccountUuid, accountId }) => ({
        url: `/preferred-supplier/${preferredAccountUuid}`,
        method: "DELETE",
        accountId,
      }),
      invalidatesTags: ["PreferredDrivers"],
    }),
  }),
});

export const { useGetBlockedDriversQuery, useUnblockDriverMutation, useGetPreferredDriversQuery, useRemovePreferredDriverMutation } =
  clientDispatchApi;
